import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {MatDialogRef} from "@angular/material/dialog";

import {TranslateService} from '@ngx-translate/core';

import {BaseComponent} from "@shared/components/base-component";
import {DialogService} from "@shared/services/dialog.service";

import {HandoverHttpService} from "@handover/http-services/handover-http.service";
import {HandoverNotificationsService} from "@handover/routes/main-route/services/handover-notifications.service";
import {HandoverModel} from '@handover/view-models/handover.model';

import {
    LoadingInitialDataComponent
} from "@layout/panel/components/loading-initial-data/loading-initial-data.component";
import {tap} from "rxjs";

@Component({
    selector: 'handoverm-handover',
    templateUrl: './handover.component.html',
    styleUrls: ['/handover.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HandoverComponent extends BaseComponent implements OnInit, OnDestroy {

    isLoading = true;
    model?: HandoverModel;
    fromOrToDriverTrans = '';
    fromDriverTrans = '';
    toDriverTrans = '';
    isPrintMode = false;
    token = '';

    private loadingInitialDataComponentRef?: MatDialogRef<any, any>;

    constructor(
        private handoverHttpService: HandoverHttpService,
        private handoverNotificationsService: HandoverNotificationsService,
        private translateService: TranslateService,
        private changeDetectorRef: ChangeDetectorRef,
        private activatedRoute: ActivatedRoute,
        private dialogService: DialogService
    ) {
        super();
        this.setTranslations();
    }

    ngOnDestroy(): void {
        this.unsubscribeAll();
    }

    ngOnInit(): void {
        this.token = this.activatedRoute.snapshot.paramMap.get('token') as string;
        if (this.token) {
            this.getHandoverByToken();
        } else {
            const givenId = this.activatedRoute.snapshot.paramMap.get('given-id') as string;
            if (givenId) {
                this.getHandoverByGivenId(givenId);
            } else
                this.subToSelectedHandoverIdChanged();
        }
    }

    private getHandoverByToken() {
        this.loadingInitialDataComponentRef = this.dialogService.open(LoadingInitialDataComponent);
        this.isPrintMode = true;
        this.handoverHttpService.getHandoverByToken(this.token).subscribe(handoverModel => {
            this.afterHandoverLoaded(handoverModel);
        });
    }

    private subToHandoverImagesLoaded() {
        if (this.model!.allImagesCount > 0) {
            this.handoverNotificationsService.handoverImagesLoaded$.subscribe(() => {
                this.loadingInitialDataComponentRef?.close();
                setTimeout(() => {
                    if (this.isPrintMode)
                        window.print();
                }, 2000);
            });
        } else {
            this.loadingInitialDataComponentRef?.close();
        }
    }

    private getHandoverByGivenId(givenId: string) {
        this.isPrintMode = true;
        const returnId = this.activatedRoute.snapshot.paramMap.get('return-id') ?? undefined;
        const returnAsNumber = isNaN(Number(returnId)) ? undefined : Number(returnId);
        this.getHandover(Number(givenId), returnAsNumber).subscribe();
    }

    private setTranslations() {
        this.translateService.get('handover.from-driver').subscribe(trans => this.fromDriverTrans = trans);
        this.translateService.get('handover.to-driver').subscribe(trans => this.toDriverTrans = trans);
    }

    private subToSelectedHandoverIdChanged() {
        this.addToUnsubscribe(
            this.handoverNotificationsService.selectedHandoverChanged$.subscribe(handoverListItem => {
                if (handoverListItem) {
                    this.getHandover(handoverListItem.handoverGivenId, handoverListItem.handoverReturnId).subscribe(() => {
                        if (this.model!.allImagesCount > 0)
                            this.loadingInitialDataComponentRef = this.dialogService.open(LoadingInitialDataComponent);
                    });
                }
            }));
    }

    private getHandover(handoverId: number, returnId?: number) {
        return this.handoverHttpService.getHandover(handoverId, returnId)
            .pipe(tap((handoverModel) => this.afterHandoverLoaded(handoverModel)));
    }

    private afterHandoverLoaded(handoverModel: HandoverModel) {
        this.model = handoverModel;
        this.subToHandoverImagesLoaded();
        this.fromOrToDriverTrans = this.model?.isGiven ? this.toDriverTrans : this.fromOrToDriverTrans;
        this.isLoading = false;
        this.changeDetectorRef.markForCheck();
    }
}
